import { Box, VStack } from '@cdl/heartbeat-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { sortedBeatsSelector } from '../../../beats/beats.selector';
import {
  baseClustersSelector,
  indexSelectedSelector,
  projectedBaseClusterSelector,
} from '../../selectors/cluster.selectors';
import { BeatColumnHeader } from './BeatColumnHeader';
import {
  BeatStrip,
  BORDER_WIDTH,
  MARGIN_BOTTOM,
  OFFSET,
  PADDING_Y,
  STRIP_BEAT_HEIGHT,
  STRIP_BEAT_WIDTH,
  TICK_WIDTH,
} from './BeatStrip';

// 6 * 8px --> 8px is the spacing(3) which is used as the default spacing in
// the app
const BEAT_COLUMN_WIDTH = STRIP_BEAT_WIDTH + TICK_WIDTH + 6 * 8;
const COLUMN_HEIGHT = '66vh';

export const BeatColumn: React.FC = () => {
  const virtualParentRef = useRef<HTMLDivElement>(null);
  const projectedCluster = useSelector(projectedBaseClusterSelector);
  const selectedIndex = useSelector(indexSelectedSelector);
  const clusters = useSelector(baseClustersSelector);
  const beats = useSelector(sortedBeatsSelector);
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string | number>('66vh');

  useEffect(() => {
    const onResize = () => {
      if (ref.current == null) {
        return COLUMN_HEIGHT;
      }
      setHeight(window.innerHeight - ref.current.offsetTop);
    };
    onResize();
    window.addEventListener('resize', debounce(onResize, 150));
    return () => window.removeEventListener('resize', onResize);
  }, [ref, clusters]);

  const size = useCallback(
    () =>
      STRIP_BEAT_HEIGHT +
      MARGIN_BOTTOM +
      2 * PADDING_Y +
      2 * OFFSET +
      2 * BORDER_WIDTH,
    [],
  );

  const rowVirtualizer = useVirtualizer({
    count: beats.length,
    getScrollElement: () => virtualParentRef.current,
    overscan: 3,
    estimateSize: size,
  });

  useEffect(() => {
    rowVirtualizer.scrollToIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  if (projectedCluster == null) {
    return null;
  }

  return (
    <VStack
      alignItems='stretch'
      width={BEAT_COLUMN_WIDTH}
      height={height}
      ref={ref}
      mt='l'
    >
      <BeatColumnHeader />
      <Box
        width='100%'
        overflow='auto'
        height={height}
        pb='m'
        ref={virtualParentRef}
      >
        <Box
          height={`${rowVirtualizer.getTotalSize()}px`}
          width='100%'
          position='relative'
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <Box
              key={virtualItem.index}
              position='absolute'
              top='0'
              left='0'
              width='100%'
              height={`${virtualItem.size}px`}
              transform={`translateY(${virtualItem.start}px)`}
            >
              <BeatStrip
                key={beats[virtualItem.index].id}
                beat={beats[virtualItem.index]}
                cluster={projectedCluster}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </VStack>
  );
};
